<template>
    <teleport to="body">
        <transition name="modal-fade" appear @enter="onEnter" @leave="onLeave">
            <div 
                v-if="showModal" 
                class="modal" 
                :class="{ show: showModal }" 
                :style="{ zIndex: computedZIndex }"
                @click.self="handleBackdropClick"
            >
                <div class="modal__dialog" :class="[`modal__dialog--${size}`]" :style="modalStyles">
                    <div role="document" class="relative h--100">
                        <div v-if="closeBtn || title" class="modal__header modal__dialog--header" :class="{ 'with__title': title }">
                            <h2 v-show="title" class="modal__header__title">
                                {{ title }}
                            </h2>
                            <button v-if="closeBtn" class="close" @click="$emit('close')">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                    viewBox="0 0 24 24" style="fill: rgba(160, 158, 167, 1);transform: ;msFilter:;"
                                >
                                    <path d="m16.192 6.344-4.243 4.242-4.242-4.242-1.414 1.414L10.535 12l-4.242 4.242 1.414 1.414 4.242-4.242 4.243 4.242 1.414-1.414L13.364 12l4.242-4.242z" />
                                </svg>
                            </button>
                        </div>
                            
                        <div class="modal__body">
                            <slot name="body" />
                        </div>
                            
                        <div v-show="footer" class="modal__footer">
                            <slot name="footer" />
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </teleport>
</template>

<script>
let modalStack = [];
let baseZIndex = 1050;

export default {
    name: "MainModal",
    
    props: {
        showModal: {
            type: Boolean,
            default: false,
        },
        closeBtn: {
            type: Boolean,
            default: true,
        },
        size: {
            type: String,
            default: "md",
            validator: (value) => {
                return [ "sm", "md", "lg"].includes(value);
            },
        },
        width: {
            type: String,
            default: "",
        },
        headerTextTransform: {
            type: String,
            default: "capitalize",
            validator: (value) => {
                return [ "capitalize", "none"].includes(value);
            },
        },
        zIndex: {
            type: String,
            default: "",
        },
        position: {
            type: String,
            default: "center",
            validator: (value) => {
                return [ "center", "top-right", "top-left", "bottom-right", "bottom-left"].includes(value);
            },
        },
        title: {
            type: String,
            default: "",
        },
        footer: {
            type: Boolean,
            default: true,
        },
    },

    emits: ["close"],

    data() {
        return {
            modalId: null,
            computedZIndex: null,
        };
    },

    computed: {
        modalStyles() {
            return {
                "width": !this.width ? "" : this.width,
                "margin-left": this.position === "center" ? "auto" : "",
                "margin-right": this.position === "center" ? "auto" : "",
            };
        },
    },

    watch: {
        showModal: {
            handler(newVal) {
                if (newVal) {
                    this.addToStack();
                } else {
                    this.removeFromStack();
                }
            },
            immediate: true,
        },
    },

    created() {
        this.modalId = this.generateId();
    },

    beforeUnmount() {
        this.removeFromStack();
    },

    methods: {
        generateId() {
            return `modal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        },

        addToStack() {
            if (!modalStack.find(modal => modal.id === this.modalId)) {
                const zIndex = this.zIndex || (baseZIndex + modalStack.length * 10);
                
                modalStack.push({
                    id: this.modalId,
                    zIndex: zIndex,
                    component: this,
                });

                this.computedZIndex = zIndex;
                this.handleBodyScroll(true);
            }
        },

        removeFromStack() {
            const index = modalStack.findIndex(modal => modal.id === this.modalId);
            
            if (index > -1) {
                modalStack.splice(index, 1);
                
                // If no modals left, restore body scroll
                if (modalStack.length === 0) {
                    this.handleBodyScroll(false);
                }
            }
        },

        handleBodyScroll(disable) {
            if (typeof document !== "undefined") {
                if (disable) {
                    document.body.style.overflow = "hidden";
                } else {
                    document.body.style.overflow = "";
                }
            }
        },

        handleBackdropClick() {
            if (this.closeOnBackdrop) {
                this.$emit("close");
            }
        },

        onEnter(el) {
            // Force reflow to ensure proper animation
            el.offsetHeight;
            
            // Add a small delay to prevent conflicts with other modal transitions
            this.$nextTick(() => {
                el.classList.add("modal-enter-active");
            });
        },

        onLeave(el) {
            el.classList.add("modal-leave-active");
        },
    },
};
</script>